import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Calendar as CalIcon, Check, X, Trash2, Plus } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "amac_admin_token";

function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking
  const [password, setPassword] = useState("");
  const [logging, setLogging] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return setAuthed(false);
    axios
      .get(`${API}/admin/me`, { headers: authHeaders() })
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      });
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    setLogging(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setAuthed(true);
      setPassword("");
    } catch (err) {
      toast.error("Password errata");
    } finally {
      setLogging(false);
    }
  };

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <p className="text-[#4A5D6B]">Caricamento…</p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="bg-white border border-[#E0DACE] p-10 w-full max-w-md space-y-6"
        >
          <div className="text-center">
            <img src={LOGO_URL} alt="AMAC" className="h-16 w-auto mx-auto mb-4" />
            <h1 className="font-serif text-2xl text-[#121C26]">Area riservata</h1>
            <p className="text-sm text-[#4A5D6B] mt-1">Accedi per gestire le prenotazioni</p>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-overline text-[#0097b2]">Password</span>
            <input
              data-testid="admin-password"
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full p-3 border border-[#E0DACE] bg-[#FAF9F6] outline-none focus:border-[#1A3B4D]"
              autoFocus
            />
          </label>
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={logging}
            className="w-full px-6 py-3 rounded-full bg-[#1A3B4D] text-[#FAF9F6] hover:bg-[#102632] disabled:opacity-60 transition-colors"
          >
            {logging ? "Accesso in corso…" : "Accedi"}
          </button>
        </form>
      </div>
    );
  }

  return <Dashboard onLogout={() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); }} />;
}

function Dashboard({ onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showBlock, setShowBlock] = useState(false);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings`, { headers: authHeaders() });
      setBookings(data);
    } catch (e) {
      toast.error("Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${id}/status`,
        { status },
        { headers: authHeaders() }
      );
      toast.success(
        status === "confirmed" ? "Confermata" : status === "rejected" ? "Rifiutata" : "Aggiornata"
      );
      fetchBookings();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa prenotazione?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, { headers: authHeaders() });
      toast.success("Eliminata");
      fetchBookings();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  const visible = bookings.filter((b) => filter === "all" || b.status === filter);
  const counts = {
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    rejected: bookings.filter((b) => b.status === "rejected").length,
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="border-b border-[#E0DACE] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="AMAC" className="h-12 w-auto" />
            <div>
              <p className="text-xs uppercase tracking-overline text-[#0097b2]">Area riservata</p>
              <p className="font-serif text-xl text-[#121C26]">Prenotazioni</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-block-btn"
              onClick={() => setShowBlock(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1A3B4D] text-[#FAF9F6] text-sm hover:bg-[#102632] transition-colors"
            >
              <Plus size={16} /> Blocca date
            </button>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E0DACE] text-[#1A3B4D] text-sm hover:bg-[#F0EDE6] transition-colors"
            >
              <LogOut size={16} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { id: "all", label: `Tutte (${bookings.length})` },
            { id: "pending", label: `In attesa (${counts.pending})` },
            { id: "confirmed", label: `Confermate (${counts.confirmed})` },
            { id: "rejected", label: `Rifiutate (${counts.rejected})` },
          ].map((f) => (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                filter === f.id
                  ? "bg-[#1A3B4D] text-white border-[#1A3B4D]"
                  : "border-[#E0DACE] text-[#4A5D6B] hover:bg-[#F0EDE6]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-[#4A5D6B] py-12">Caricamento…</p>
        ) : visible.length === 0 ? (
          <p className="text-center text-[#4A5D6B] py-12">Nessuna prenotazione</p>
        ) : (
          <div className="space-y-3">
            {visible.map((b) => (
              <BookingCard
                key={b.id}
                booking={b}
                onConfirm={() => setStatus(b.id, "confirmed")}
                onReject={() => setStatus(b.id, "rejected")}
                onReset={() => setStatus(b.id, "pending")}
                onDelete={() => remove(b.id)}
              />
            ))}
          </div>
        )}
      </main>

      {showBlock && <BlockDatesModal onClose={() => setShowBlock(false)} onSaved={fetchBookings} />}
    </div>
  );
}

function BookingCard({ booking, onConfirm, onReject, onReset, onDelete }) {
  const statusColor =
    booking.status === "confirmed"
      ? "bg-[#0097b2] text-white"
      : booking.status === "rejected"
      ? "bg-[#c44]/15 text-[#8b2929]"
      : "bg-[#F0EDE6] text-[#4A5D6B]";

  return (
    <article
      data-testid={`booking-${booking.id}`}
      className="bg-white border border-[#E0DACE] p-6 grid md:grid-cols-12 gap-4 items-start"
    >
      <div className="md:col-span-4">
        <p className="font-serif text-lg text-[#121C26]">{booking.name}</p>
        <a href={`mailto:${booking.email}`} className="block text-sm text-[#0097b2] hover:underline">
          {booking.email}
        </a>
        {booking.phone && (
          <a href={`tel:${booking.phone}`} className="block text-sm text-[#0097b2] hover:underline">
            {booking.phone}
          </a>
        )}
        <p className="text-xs text-[#4A5D6B]/70 mt-1">Ricevuta: {formatDate(booking.created_at)}</p>
      </div>
      <div className="md:col-span-3">
        <p className="text-xs uppercase tracking-overline text-[#0097b2]">Date</p>
        <p className="font-serif text-base text-[#121C26]">
          {booking.check_in} → {booking.check_out}
        </p>
        <p className="text-sm text-[#4A5D6B]">{booking.guests} ospiti</p>
      </div>
      <div className="md:col-span-3 text-sm text-[#4A5D6B] whitespace-pre-wrap">
        {booking.message || <span className="opacity-50">—</span>}
      </div>
      <div className="md:col-span-2 flex flex-col items-stretch gap-2">
        <span className={`inline-block text-center px-3 py-1 rounded-full text-xs ${statusColor}`}>
          {booking.status}
        </span>
        <div className="flex gap-1.5 flex-wrap">
          {booking.status !== "confirmed" && (
            <button
              data-testid={`confirm-${booking.id}`}
              onClick={onConfirm}
              title="Conferma"
              className="p-2 rounded-full bg-[#0097b2]/10 text-[#0097b2] hover:bg-[#0097b2] hover:text-white transition-colors"
            >
              <Check size={14} />
            </button>
          )}
          {booking.status !== "rejected" && (
            <button
              data-testid={`reject-${booking.id}`}
              onClick={onReject}
              title="Rifiuta"
              className="p-2 rounded-full bg-[#c44]/10 text-[#8b2929] hover:bg-[#8b2929] hover:text-white transition-colors"
            >
              <X size={14} />
            </button>
          )}
          {booking.status !== "pending" && (
            <button
              data-testid={`reset-${booking.id}`}
              onClick={onReset}
              title="Rimetti in attesa"
              className="p-2 rounded-full bg-[#F0EDE6] text-[#4A5D6B] hover:bg-[#4A5D6B] hover:text-white transition-colors"
            >
              <CalIcon size={14} />
            </button>
          )}
          <button
            data-testid={`delete-${booking.id}`}
            onClick={onDelete}
            title="Elimina"
            className="p-2 rounded-full border border-[#E0DACE] text-[#4A5D6B] hover:bg-[#c44] hover:text-white hover:border-[#c44] transition-colors"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </article>
  );
}

function BlockDatesModal({ onClose, onSaved }) {
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/block-dates`,
        { check_in: checkIn, check_out: checkOut, note },
        { headers: authHeaders() }
      );
      toast.success("Date bloccate");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#121C26]/60 flex items-center justify-center p-4">
      <form
        onSubmit={submit}
        data-testid="block-dates-form"
        className="bg-white border border-[#E0DACE] p-8 w-full max-w-md space-y-5"
      >
        <div>
          <p className="text-xs uppercase tracking-overline text-[#0097b2]">Blocco manuale</p>
          <h3 className="font-serif text-2xl text-[#121C26]">Blocca date sul calendario</h3>
          <p className="text-sm text-[#4A5D6B] mt-1">
            Le date selezionate appariranno in rosso sul calendario pubblico.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs uppercase tracking-overline text-[#0097b2]">Check-in</span>
            <input
              data-testid="block-checkin"
              required
              type="date"
              value={checkIn}
              onChange={(e) => setCheckIn(e.target.value)}
              className="mt-2 w-full p-3 border border-[#E0DACE] bg-[#FAF9F6] outline-none focus:border-[#1A3B4D]"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-overline text-[#0097b2]">Check-out</span>
            <input
              data-testid="block-checkout"
              required
              type="date"
              value={checkOut}
              onChange={(e) => setCheckOut(e.target.value)}
              className="mt-2 w-full p-3 border border-[#E0DACE] bg-[#FAF9F6] outline-none focus:border-[#1A3B4D]"
            />
          </label>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-overline text-[#0097b2]">Nota (opz.)</span>
          <input
            data-testid="block-note"
            placeholder="Es. Familiari · Booking.com · Pulizia"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="mt-2 w-full p-3 border border-[#E0DACE] bg-[#FAF9F6] outline-none focus:border-[#1A3B4D]"
          />
        </label>
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-full border border-[#E0DACE] text-[#4A5D6B] hover:bg-[#F0EDE6]"
          >
            Annulla
          </button>
          <button
            type="submit"
            data-testid="block-submit"
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-full bg-[#1A3B4D] text-[#FAF9F6] hover:bg-[#102632] disabled:opacity-60"
          >
            {saving ? "Salvo…" : "Blocca date"}
          </button>
        </div>
      </form>
    </div>
  );
}
